import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { API_VERSION } from "@/lib/apiVersion";
import type { SeasonType } from "@/lib/seasonUtils";

export type LeagueGender = 'men' | 'women';

export interface LeagueInfo {
  code: string;
  displayName: string;
  seasonType: SeasonType;
  tier: string | null;
  gender: LeagueGender;
}

export const useActiveLeagues = () => {
  return useQuery({
    queryKey: ['active-leagues', API_VERSION],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leagues')
        .select('code, display_name, season_type, tier, gender')
        .eq('is_active', true);
      
      if (error) {
        console.error('[useActiveLeagues] Failed to fetch active leagues:', error);
        throw error;
      }
      
      const leagues: LeagueInfo[] = (data || []).map(l => ({
        code: l.code,
        displayName: l.display_name,
        seasonType: (l.season_type as SeasonType) || 'split-year',
        tier: l.tier,
        gender: (l.gender === 'women' ? 'women' : 'men') as LeagueGender,
      }));
      
      console.log(`[useActiveLeagues] Loaded ${leagues.length} active leagues`);
      return leagues;
    },

    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
};

/**
 * Helper to get just the league codes from the active leagues list.
 */
export const getActiveLeagueCodes = (leagues: LeagueInfo[] | undefined): string[] => {
  return leagues?.map(l => l.code) || [];
};

/**
 * Helper to get season type for a specific league.
 */
export const getLeagueSeasonType = (
  leagueCode: string,
  leagues: LeagueInfo[] | undefined
): SeasonType => {
  const league = leagues?.find(l => l.code === leagueCode);
  return league?.seasonType || 'split-year';
};

/**
 * Check if a league is in the active leagues list.
 * Returns true if activeLeagues is empty (graceful fallback).
 */
export const isLeagueSupportedDynamic = (
  leagueCode: string, 
  activeLeagues: LeagueInfo[] | string[]
): boolean => {
  // If activeLeagues is empty/unavailable, treat as "unknown" and don't filter
  if (!activeLeagues || activeLeagues.length === 0) {
    return true;
  }
  
  // Handle both LeagueInfo[] and string[] for backwards compatibility
  if (typeof activeLeagues[0] === 'string') {
    return (activeLeagues as string[]).includes(leagueCode);
  }
  
  return (activeLeagues as LeagueInfo[]).some(l => l.code === leagueCode);
};

/**
 * Filter a league list by gender (men's game by default).
 */
export const filterLeaguesByGender = (
  leagues: LeagueInfo[] | undefined,
  gender: LeagueGender = 'men'
): LeagueInfo[] => (leagues || []).filter(l => (l.gender || 'men') === gender);
